/**
 * «Активность»: сотрудники КЦ за день — часы, акцепт, звонки, сообщения. Спеки
 * `docs/specs/2026-09-24-activity-design.md` и
 * `docs/specs/2026-09-24-activity-messenger-design.md`; числа — те же, что у
 * `prokat-runner activity-report`. Период, сотрудник и разбивка по дням, исходящие по лидам и
 * сделкам со ссылками в «Звонки» — `docs/specs/2026-10-19-activity-period-design.md`. Вкладка «По
 * дням» (`?view=days`) — хватает ли людей в смене: `docs/specs/2026-10-26-activity-days-design.md`.
 * Вкладка «По часам» (`?view=hours`) — как прошёл один день: `docs/specs/2026-11-03-activity-hours-design.md`.
 * Вкладка «Контроль» (`?view=control`, право `activity_control`) — аномалии смены:
 * `docs/specs/2026-11-03-activity-control-design.md`.
 */
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Fragment, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

import { ControlView, KINDS, type StatusFilter } from "@/components/activity/ControlView";
import { DaysView } from "@/components/activity/DaysView";
import { HoursView } from "@/components/activity/HoursView";
import { Combo } from "@/components/Combo";
import { DateRangePicker, прошлыеОкна } from "@/components/DateRangePicker";
import { ACTIVITY_CONTROL, useCan } from "@/lib/access";
import {
  type ActivityGroup,
  type ActivityReport,
  type ControlKind,
  type Line,
  loadActivity,
  type Person,
  type Totals,
} from "@/lib/activity";
import { addDays } from "@/lib/delivery";
import { workTime, yesterday } from "@/lib/leads";

function Tile({
  label,
  value,
  hint,
}: {
  label: string;
  value: ReactNode;
  hint?: string;
}) {
  return (
    <div
      className="flex min-w-0 flex-col gap-1 rounded-[var(--radius-card)] border border-border bg-bg-subtle p-4"
      data-annot={`плитка «${label}»`}
    >
      <div className="text-[12px] text-fg-muted">{label}</div>
      <div className="text-[26px] font-semibold leading-none tracking-[-0.02em] tabular-nums">
        {value}
      </div>
      {hint && <div className="text-[12px] text-fg-faint">{hint}</div>}
    </div>
  );
}

const hhmm = (iso: string | null) => (iso ? iso.slice(11, 16) : "");

/** Где открыть звонки под числом: период строки (день или весь отрезок). */
type Ctx = { from: string; to: string };

/**
 * Колонка таблицы: заголовок, подсказка, значение строки и итога. `group` — общий заголовок над
 * несколькими колонками (исходящие по лидам, по сделкам).
 */
type Column = {
  key: string;
  head: string;
  hint: string;
  group?: string;
  cell: (p: Person, ctx: Ctx) => ReactNode;
  total: (t: Totals, line: Line) => ReactNode;
};

/** «Не дошло» под числом — аномалии этого сотрудника во вкладке «Контроль» (если она видна). */
function UnreachedNum({ n, ctx, user }: { n: number; ctx: Ctx; user: number }) {
  const can = useCan(ACTIVITY_CONTROL);
  if (!n) return <>0</>;
  if (!can) return <span className="text-warn">{n}</span>;
  const q = new URLSearchParams({ from: ctx.from, to: ctx.to, view: "control", user: String(user), kind: "unreached", status: "all" });
  return (
    <Link
      to={`/activity?${q}`}
      onClick={(e) => e.stopPropagation()}
      className="text-warn hover:underline"
      title="открыть эти аномалии во вкладке «Контроль»"
    >
      {n}
    </Link>
  );
}

const today = () => addDays(yesterday(), 1);

/** Исходящие под числом — в «Звонки» (`/calls?tab=list&act=…`): там слушают запись и читают расшифровку. */
function callsHref(ctx: Ctx, user: number, act: string, step: string): string {
  const q = new URLSearchParams({ tab: "list", from: ctx.from });
  if (ctx.to !== ctx.from) q.set("to", ctx.to);
  q.set("act", act);
  q.set("auser", String(user));
  if (step !== "all") q.set("astep", step);
  return `/calls?${q}`;
}

/** Число-ссылка на звонки; ноль — точкой, открывать нечего. Клик не открывает смену строки. */
function CallsNum({ n, href, title }: { n: number; href: string; title?: string }) {
  if (!n) return <span className="text-fg-faint">·</span>;
  return (
    <Link
      to={href}
      onClick={(e) => e.stopPropagation()}
      title={title ?? "открыть эти звонки: запись и расшифровка"}
      className="text-accent hover:underline"
    >
      {n}
    </Link>
  );
}

const dash = (v: ReactNode | null) => (v === null ? "—" : v);

/** Три колонки группы «исходящие по …»: звонков, дозвон (разговор по разметке), записали. */
function dialColumns(kind: "lead" | "deal", group: string, what: string): Column[] {
  const talk = `${kind}_talk` as const;
  const zapis = `${kind}_zapis` as const;
  return [
    {
      key: kind,
      head: "звонков",
      group,
      hint: `исходящие ${what}`,
      cell: (p, ctx) => <CallsNum n={p.dial[kind]} href={callsHref(ctx, p.user_id, kind, "all")} />,
      total: (t) => t.dial[kind],
    },
    {
      key: talk,
      head: "дозвон",
      group,
      hint: `исходящие ${what}, где состоялся разговор с клиентом — по разметке расшифровки, а не по коду 200: автоответчик, голосовой ассистент, «Алло» в тишину — не разговор`,
      cell: (p, ctx) => (
        <CallsNum n={p.dial[talk]} href={callsHref(ctx, p.user_id, kind, "talk")} title="открыть разговоры: о чём говорили" />
      ),
      total: (t) => t.dial[talk],
    },
    {
      key: zapis,
      head: "записали",
      group,
      hint: `исходящие ${what}, после которых тот же сотрудник перевёл сделку в «Ждем клиента» — в окне звонка: до 15 минут после конца и не позже его следующего звонка`,
      cell: (p, ctx) => (
        <CallsNum n={p.dial[zapis]} href={callsHref(ctx, p.user_id, kind, "zapis")} title="открыть звонки, после которых записали" />
      ),
      total: (t) => t.dial[zapis],
    },
  ];
}

const COLUMNS: Column[] = [
  {
    key: "shift",
    head: "Смена",
    hint: "первый и последний статус дня, кроме «Ушел» по краям; за период — сколько дней сотрудник в отчёте",
    cell: (p) =>
      p.shift_from ? `${hhmm(p.shift_from)}–${hhmm(p.shift_to)}` : p.days > 1 ? `${p.days} дн.` : "—",
    total: () => "",
  },
  {
    key: "paid",
    head: "Часы",
    hint: "оплачиваемые статусы: Работаю, Задача(В очереди), Задача(Без очереди)",
    cell: (p) => workTime(p.paid_min),
    total: (t) => workTime(t.paid_min),
  },
  {
    key: "line",
    head: "На линии",
    hint: "статусы, в которых сотруднику распределяют входящие: Работаю, Задача(В очереди)",
    cell: (p) => workTime(p.line_min),
    total: () => "",
  },
  {
    key: "accept",
    head: "Акцепт",
    hint: "подтверждённое время из CRM; CRM досчитывает его через несколько дней — до того прочерк",
    cell: (p) =>
      p.accept_min === null ? (
        <span className="text-fg-faint">—</span>
      ) : (
        workTime(p.accept_min)
      ),
    total: (t) => (t.accept_min === null ? "—" : workTime(t.accept_min)),
  },
  {
    key: "calls",
    head: "Звонков",
    hint: "исходящие, принятые входящие и прочие; непринятые входящие не считаются",
    cell: (p) => <span className="font-medium">{p.calls.total}</span>,
    total: (t) => t.calls,
  },
  {
    key: "out",
    head: "Исход.",
    hint: "исходящие звонки — все: по лидам, по сделкам и прочие",
    cell: (p, ctx) => <CallsNum n={p.calls.out} href={callsHref(ctx, p.user_id, "out", "all")} />,
    total: (t) => t.out,
  },
  {
    key: "talk",
    head: "Дозвон",
    hint: "исходящие, где состоялся разговор с клиентом — по разметке расшифровки (`bitrix_call_talk`), а не по коду 200 и не по длительности: автоответчик, голосовой ассистент, монолог в тишину — не разговор. Клик — эти разговоры с расшифровкой",
    cell: (p, ctx) => (
      <span title={p.dial.pending ? `ещё не размечено: ${p.dial.pending} — свежие звонки ждут расшифровки` : undefined}>
        <CallsNum n={p.dial.talk} href={callsHref(ctx, p.user_id, "out", "talk")} title="открыть разговоры: о чём говорили" />
        {p.dial.pending ? <span className="text-fg-faint"> +{p.dial.pending}?</span> : null}
      </span>
    ),
    total: (t) => t.dial.talk,
  },
  ...dialColumns("lead", "Исходящие по лидам", "по лидам: звонили лиду, сделки на момент звонка ещё не было"),
  ...dialColumns("deal", "Исходящие по сделкам", "по сделкам: сделка уже была к началу звонка (перезвон, обзвон)"),
  {
    key: "in",
    head: "Вход.",
    hint: "принятые входящие, включая переведённые",
    cell: (p) => p.calls.in,
    total: (t) => t.in,
  },
  {
    key: "missed",
    head: "Пропущено",
    hint: "непринятые входящие, пока сотрудник был на линии и не разговаривал хотя бы 5 секунд из времени звонка; в итоге — разных звонков, не сумма по людям",
    cell: (p) => (p.missed ? <span className="text-warn">{p.missed}</span> : 0),
    total: (_, line) => line.missed_calls,
  },
  {
    key: "unreached",
    head: "Не дошло",
    hint: "из пропущенных: статус на линии и свободен, очередь отдавала вызов от 10 секунд, но телефон не в сети и Bitrix в браузере не ответил — звонок до человека не дошёл. Клик — эти аномалии во вкладке «Контроль»; в итоге — разных звонков",
    cell: (p, ctx) => <UnreachedNum n={p.unreached ?? 0} ctx={ctx} user={p.user_id} />,
    total: (_, line) => line.unreached_calls ?? "—",
  },
  {
    key: "talk_sec",
    head: "Разговор",
    hint: "сумма длительности разговоров",
    cell: (p) => workTime(p.talk_sec / 60),
    total: (t) => workTime(t.talk_sec / 60),
  },
  {
    key: "handle",
    head: "На звонках",
    hint: "разговоры + дозвон исходящих + 1 минута на каждый звонок (подготовка и обработка: история сделки, итог)",
    cell: (p) => workTime(p.handle_sec / 60),
    total: (t) => workTime(t.handle_sec / 60),
  },
  {
    key: "busy",
    head: "Занятость",
    hint: "по действиям: звонки (минута после разговора, 15 с после недозвона) и 30 с на каждое отправленное сообщение, совпавшие по времени — один раз, / оплачиваемые часы. Панель расширения не входит (она не у всех) — с ней в подсказке ячейки; меньше 20% подсвечено",
    cell: (p) =>
      // `?.` — ответ сервера до выкатки занятости по действиям поля `active` не несёт.
      p.active?.pct == null ? (
        "—"
      ) : (
        <span
          className={p.active.pct < 20 ? "text-warn" : ""}
          title={`с панелью расширения ${p.active.with_panel_pct ?? "—"}%${p.active.ext ? "" : " · расширения нет"}`}
        >
          {p.active.pct}%
        </span>
      ),
    total: (t) => (t.active?.pct == null ? "—" : `${t.active.pct}%`),
  },
  {
    key: "busy_calls",
    head: "На звонках",
    hint: "прежняя формула: разговор + дозвон исходящего + минута на КАЖДЫЙ звонок / часы; переписка не входит, недозвоны её раздувают",
    cell: (p) => (p.busy_pct === null ? "—" : `${p.busy_pct}%`),
    total: (t) => (t.busy_pct === null ? "—" : `${t.busy_pct}%`),
  },
  {
    key: "per_hour",
    head: "В час",
    hint: "звонков на час оплачиваемого времени",
    cell: (p) => p.per_hour ?? "—",
    total: (t) => dash(t.per_hour),
  },
  {
    key: "idle",
    head: "Простой",
    hint: "оплачиваемое время, в котором не было ни своего звонка, ни отправки сообщения, — отрезками от 10 минут. Тот же простой, что в детализации смены; клик по строке её открывает",
    cell: (p) =>
      p.idle_min ? (
        <span className={p.idle_min > 120 * p.days ? "text-err" : p.idle_min > 60 * p.days ? "text-warn" : ""} title={`отрезков: ${p.idle_gaps}`}>
          {workTime(p.idle_min)}
        </span>
      ) : (
        <span className="text-fg-faint">·</span>
      ),
    total: (t) => (t.idle_min ? workTime(t.idle_min) : "—"),
  },
  {
    key: "sent",
    head: "Сообщений",
    hint: "уникальные отправки: скрипт CRM, раздел «Чаты» и расширение Chrome; двойные записи CRM (тот же текст тому же получателю в пределах минуты) и отправка, записанная и CRM, и расширением, считаются один раз",
    cell: (p) => (
      <span
        className="font-medium"
        title={p.messenger.dups ? `отсеяно дублей CRM: ${p.messenger.dups}` : undefined}
      >
        {p.messenger.sent}
      </span>
    ),
    total: (t) => t.messenger.sent,
  },
  { key: "wa", head: "WA", hint: "WhatsApp: web.whatsapp, wazzup и «Чаты»", cell: (p) => p.messenger.wa, total: (t) => t.messenger.wa },
  { key: "tg", head: "TG", hint: "Telegram", cell: (p) => p.messenger.tg, total: (t) => t.messenger.tg },
  {
    key: "max",
    head: "MAX",
    hint: "MAX: расширение Chrome и «Чаты»; CRM не пишет отправки из MAX с 27.07.2026",
    cell: (p) => p.messenger.max,
    total: (t) => t.messenger.max,
  },
  {
    key: "places",
    head: "Площадки",
    hint: "Авито, ВКонтакте и Instagram — отправки из раздела «Чаты»",
    cell: (p) => (
      <span title={`Авито ${p.messenger.avito}, ВК ${p.messenger.vk}, Instagram ${p.messenger.ig}`}>
        {places(p.messenger)}
      </span>
    ),
    total: (t) => places(t.messenger),
  },
  {
    key: "groups",
    head: "Группы",
    hint: "сообщения в ГРУППОВЫХ чатах (Общее <город>, Head…, Новости КЦ) — внутренняя переписка, с клиентскими не смешивается; видна в «Чатах» под фильтром «Группы»",
    cell: (p) =>
      p.groups.msgs ? (
        <span title={`разных групп ${p.groups.chats}`}>{p.groups.msgs}</span>
      ) : (
        <span className="text-fg-faint">·</span>
      ),
    total: (t) => t.groups.msgs,
  },
  {
    key: "contacts",
    head: "Обращений",
    hint: "клиент написал после 72 ч тишины, и сотрудник ответил на это первым: автор — из кабинета или из события расширения Chrome. Разбор целиком — раздел «Чаты · Аналитика»",
    cell: (p) => (
      <span className="font-medium" title={`разных диалогов ${p.chats.chats}`}>
        {p.chats.contacts || <span className="text-fg-faint">·</span>}
      </span>
    ),
    total: (t) => t.chats.contacts,
  },
  {
    key: "answer",
    head: "Ответ",
    hint: "медиана времени ответа на обращение; считается только в рабочие часы 10:00–21:00 — ночь на сотрудника не вешаем",
    cell: (p) =>
      p.chats.median_sec === null ? (
        <span className="text-fg-faint">·</span>
      ) : (
        <span className={p.chats.median_sec > 900 ? "text-warn" : p.chats.median_sec <= 120 ? "text-ok" : ""}>
          {пауза(p.chats.median_sec)}
        </span>
      ),
    total: (t) => (t.chats.median_sec === null ? "—" : пауза(t.chats.median_sec)),
  },
  {
    key: "fast",
    head: "≤ 2 мин",
    hint: "сколько обращений закрыто ответом за две минуты и меньше",
    cell: (p) =>
      p.chats.contacts ? (
        <span title={`${Math.round((100 * p.chats.fast) / p.chats.contacts)}% обращений`}>{p.chats.fast}</span>
      ) : (
        <span className="text-fg-faint">·</span>
      ),
    total: (t) => t.chats.fast,
  },
];

/** 189 → «3,2 мин», 42 → «42 с». */
function пауза(sec: number): string {
  if (sec < 60) return `${sec} с`;
  const мин = sec / 60;
  return мин < 10 ? `${мин.toFixed(1).replace(".", ",")} мин` : `${Math.round(мин)} мин`;
}

/** Авито + ВК + Instagram: площадки, куда пишут только из «Чатов». */
const places = (m: { avito: number; vk: number; ig: number }) => m.avito + m.vk + m.ig;

/** Части времени по воронке: подпись, подсказка и откуда брать проценты. */
const TIME_PARTS = [
  { key: "in_pct", sec: "in_sec", head: "Входящие", hint: "разговор + минута обработки каждого входящего" },
  { key: "out_lead_pct", sec: "out_lead_sec", head: "Исх. по лидам", hint: "исходящие, привязанные к лиду: разговор + дозвон + минута обработки" },
  {
    key: "out_deal_pct",
    sec: "out_deal_sec",
    head: "Обзвон сделок",
    hint: "исходящие по сделкам, которые НА МОМЕНТ звонка были в стадии «В работе» или «Думают» — тот самый обзвон",
  },
  { key: "out_rest_pct", sec: "out_rest_sec", head: "Прочие исх.", hint: "исходящие без лида и без сделки в обзвоне, а также служебные типы звонков" },
  {
    key: "group_pct",
    sec: "group_sec",
    head: "Группы",
    hint: "переписка в групповых чатах сотрудников, те же 30 секунд на сообщение",
  },
  {
    key: "chat_pct",
    sec: "chat_sec",
    head: "Чаты",
    hint: "по 30 секунд на каждое отправленное сообщение — допущение, а не замер: столько в среднем уходит на один ответ",
  },
] as const;

/** «Куда уходит время» — процентовка оплачиваемого часа по воронке, отдельной таблицей. */
function TimeSection({ r }: { r: ActivityReport }) {
  const navigate = useNavigate();
  const t = r.totals.time;
  const тон = (v: number | null) => (v === null ? "" : v >= 40 ? "text-err" : v >= 20 ? "text-warn" : "");
  return (
    <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle" data-annot="куда уходит время">
      <div className="flex flex-wrap items-baseline gap-x-3 border-b border-border px-4 py-2.5">
        <h2 className="text-[13px] font-medium">Куда уходит время</h2>
        <span className="text-[11.5px] text-fg-faint">
          доля оплачиваемого времени; чаты — по 30 с на отправленное сообщение (допущение). Остаток до 100 % — простой
          и всё, что мы не измеряем
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead className="text-[11.5px] text-fg-muted">
            <tr>
              <th className="whitespace-nowrap border-b border-border px-4 py-2 text-left font-medium">Сотрудник</th>
              <th className="whitespace-nowrap border-b border-border px-2.5 py-2 text-right font-medium">Часы</th>
              {TIME_PARTS.map((c) => (
                <th
                  key={c.key}
                  title={c.hint}
                  className="whitespace-nowrap border-b border-border px-2.5 py-2 text-right font-medium"
                >
                  {c.head}
                </th>
              ))}
              <th
                title="сумма измеренных частей: столько времени смены мы видим в цифрах"
                className="whitespace-nowrap border-b border-border px-2.5 py-2 text-right font-medium"
              >
                Видно всего
              </th>
              <th
                title="оплачиваемое время без звонка и без отправки, отрезками от 10 минут"
                className="whitespace-nowrap border-b border-border px-2.5 py-2 text-right font-medium"
              >
                Простой
              </th>
            </tr>
          </thead>
          <tbody>
            {r.people.map((p) => (
              <tr
                key={p.user_id}
                onClick={() => navigate(`/activity/${p.user_id}?date=${r.to}`)}
                className="cursor-pointer border-b border-border last:border-b-0 hover:bg-bg-hover"
                data-annot={`время ${p.name}`}
              >
                <td className="whitespace-nowrap px-4 py-1.5">{p.name}</td>
                <td className="whitespace-nowrap px-2.5 py-1.5 text-right tabular-nums text-fg-muted">
                  {workTime(p.paid_min)}
                </td>
                {TIME_PARTS.map((c) => (
                  <td key={c.key} className="whitespace-nowrap px-2.5 py-1.5 text-right tabular-nums">
                    {p.time[c.key] === null || p.time[c.key] === 0 ? (
                      <span className="text-fg-faint">·</span>
                    ) : (
                      <span className={тон(p.time[c.key])} title={workTime(p.time[c.sec] / 60)}>
                        {p.time[c.key]}%
                      </span>
                    )}
                  </td>
                ))}
                <td className="whitespace-nowrap px-2.5 py-1.5 text-right font-medium tabular-nums">
                  {p.time.total_pct === null ? "—" : `${p.time.total_pct}%`}
                </td>
                <td className="whitespace-nowrap px-2.5 py-1.5 text-right tabular-nums text-fg-muted">
                  {p.idle_min ? workTime(p.idle_min) : <span className="text-fg-faint">·</span>}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="border-t border-border-strong font-medium">
            <tr>
              <td className="whitespace-nowrap px-4 py-2">Итого</td>
              <td className="whitespace-nowrap px-2.5 py-2 text-right tabular-nums">{workTime(r.totals.paid_min)}</td>
              {TIME_PARTS.map((c) => (
                <td key={c.key} className="whitespace-nowrap px-2.5 py-2 text-right tabular-nums">
                  {t[c.key] === null ? "—" : `${t[c.key]}%`}
                </td>
              ))}
              <td className="whitespace-nowrap px-2.5 py-2 text-right tabular-nums">
                {t.total_pct === null ? "—" : `${t.total_pct}%`}
              </td>
              <td className="whitespace-nowrap px-2.5 py-2 text-right tabular-nums">
                {r.totals.idle_min ? workTime(r.totals.idle_min) : "—"}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
}

const WEEKDAY = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"];
/** «пт 17.10» */
const dayTitle = (day: string) => `${WEEKDAY[new Date(`${day}T12:00:00`).getDay()]} ${day.slice(8, 10)}.${day.slice(5, 7)}`;

const cellCls = "whitespace-nowrap px-2.5 py-1.5 text-right tabular-nums";

/** Строка сотрудника; `label` — что в первой ячейке вместо имени (дата при одном сотруднике по дням). */
function PersonRow({ p, ctx, label }: { p: Person; ctx: Ctx; label?: string }) {
  const navigate = useNavigate();
  return (
    <tr
      className="cursor-pointer border-t border-border hover:bg-bg-hover"
      data-annot={`сотрудник ${p.user_id}`}
      title={`Открыть смену за ${dayTitle(ctx.to)}: статусы, звонки, простои по часам`}
      onClick={() => navigate(`/activity/${p.user_id}?date=${ctx.to}`)}
    >
      <td className="px-4 py-1.5">
        {label ? (
          <div className="whitespace-nowrap">{label}</div>
        ) : (
          <>
            <div className="whitespace-nowrap">{p.name}</div>
            <div className="text-[11px] text-fg-faint">{p.location}</div>
          </>
        )}
      </td>
      {COLUMNS.map((c) => (
        <td key={c.key} className={cellCls} data-annot={c.group ? `${c.group} · ${c.head}` : c.head}>
          {c.cell(p, ctx)}
        </td>
      ))}
    </tr>
  );
}

/** Шапка в два яруса: колонки группы — под общим заголовком, остальные — на оба яруса. */
function Head() {
  const верх: { key: string; group?: string; span: number; c?: Column }[] = [];
  for (const c of COLUMNS) {
    const last = верх[верх.length - 1];
    if (c.group && last?.group === c.group) last.span += 1;
    else верх.push(c.group ? { key: c.key, group: c.group, span: 1 } : { key: c.key, span: 1, c });
  }
  const th = "cursor-help whitespace-nowrap px-2.5 py-1 text-right font-medium underline decoration-dotted underline-offset-2";
  return (
    <thead className="text-left text-[11.5px] text-fg-faint">
      <tr>
        <th rowSpan={2} className="px-4 py-2 align-bottom font-medium">
          Сотрудник
        </th>
        {верх.map((v) =>
          v.c ? (
            <th key={v.key} rowSpan={2} title={v.c.hint} data-annot={`колонка «${v.c.head}»`} className={`${th} align-bottom`}>
              {v.c.head}
            </th>
          ) : (
            <th key={v.key} colSpan={v.span} className="whitespace-nowrap border-b border-border px-2.5 pt-2 pb-0.5 text-center font-medium text-fg-muted" data-annot={`группа «${v.group}»`}>
              {v.group}
            </th>
          ),
        )}
      </tr>
      <tr>
        {COLUMNS.filter((c) => c.group).map((c) => (
          <th key={c.key} title={c.hint} data-annot={`колонка «${c.group} · ${c.head}»`} className={th}>
            {c.head}
          </th>
        ))}
      </tr>
    </thead>
  );
}

function Report({ r, user }: { r: ActivityReport; user: string }) {
  const { totals: t, line } = r;
  const period: Ctx = { from: r.from, to: r.to };
  const byDay = r.group === "day";
  const days = r.days.filter((d) => d.people.length > 0);
  const empty = byDay ? days.length === 0 : r.people.length === 0;
  return (
    <>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Tile
          label="Сотрудников"
          value={t.people}
          hint={`часов всего ${workTime(t.paid_min)}`}
        />
        <Tile
          label="Звонков"
          value={t.calls}
          hint={`исходящих ${t.out} (дозвон ${t.dial.talk}, записали ${t.dial.zapis}), входящих ${t.in}; в час ${t.per_hour ?? "—"}`}
        />
        <Tile
          label="Входящих на линию"
          value={line.incoming}
          hint={`принято ${line.incoming_answered}, не принято ${line.incoming_unanswered}`}
        />
        <Tile
          label="Пропущенных звонков"
          value={line.missed_calls}
          hint="непринятых, когда кто-то был на линии и свободен; разных звонков, не сумма по людям"
        />
        <Tile
          label="Сообщений"
          value={t.messenger.sent}
          hint={`WhatsApp ${t.messenger.wa}, Telegram ${t.messenger.tg}, MAX ${t.messenger.max}, Авито ${t.messenger.avito}, ВК ${t.messenger.vk}, Instagram ${t.messenger.ig}; дублей CRM отсеяно ${t.messenger.dups}`}
        />
      </div>

      <section
        className="rounded-[var(--radius-card)] border border-border bg-bg-subtle"
        data-annot="сотрудники"
      >
        {empty ? (
          <div className="p-4 text-[12.5px] text-fg-muted">
            За этот период нет ни часов, ни звонков.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <Head />
              <tbody>
                {!byDay &&
                  r.people.map((p) => <PersonRow key={p.user_id} p={p} ctx={period} />)}
                {byDay &&
                  days.map((d) => {
                    const ctx = { from: d.day, to: d.day };
                    // Один сотрудник — строка на день с датой вместо имени; иначе день — заголовком.
                    if (user)
                      return d.people.map((p) => <PersonRow key={`${d.day}-${p.user_id}`} p={p} ctx={ctx} label={dayTitle(d.day)} />);
                    return (
                      <Fragment key={d.day}>
                        <tr className="border-t border-border-strong bg-bg" data-annot={`день ${d.day}`}>
                          <td colSpan={COLUMNS.length + 1} className="px-4 py-1.5 text-[12px] font-medium">
                            {dayTitle(d.day)}
                            <span className="ml-2 font-normal text-fg-faint">
                              сотрудников {d.totals.people} · исходящих {d.totals.out} · дозвон {d.totals.dial.talk} · записали {d.totals.dial.zapis}
                            </span>
                          </td>
                        </tr>
                        {d.people.map((p) => (
                          <PersonRow key={`${d.day}-${p.user_id}`} p={p} ctx={ctx} />
                        ))}
                      </Fragment>
                    );
                  })}
              </tbody>
              <tfoot className="border-t border-border-strong font-medium">
                <tr data-annot="итог">
                  <td className="px-4 py-2">{r.from === r.to ? "Итого" : "Итого за период"}</td>
                  {COLUMNS.map((c) => (
                    <td key={c.key} className="whitespace-nowrap px-2.5 py-2 text-right tabular-nums">
                      {c.total(t, line)}
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </section>
      <TimeSection r={r} />
      <div className="text-[12px] text-fg-faint">
        В отчёте — только сотрудники «Контакт Центра» и салонов: кто звонил, писал в мессенджерах или (КЦ)
        был в оплачиваемых статусах. Наведите на заголовок колонки — там правило подсчёта; синие цифры
        исходящих открывают сами звонки с записью и расшифровкой; клик по строке — смена сотрудника по
        часам.
      </div>
    </>
  );
}

/** Окна быстрого выбора: период не длиннее 31 дня — «год» и «2 года» сервер не примет. */
const окна = () => прошлыеОкна().filter((o) => (Date.parse(o.to) - Date.parse(o.from)) / 86_400_000 < 31);

const GROUPS: Record<ActivityGroup, string> = { period: "за период", day: "по дням" };

type View = "staff" | "days" | "hours" | "control";
const VIEWS: { id: View; label: string }[] = [
  { id: "staff", label: "По сотрудникам" },
  { id: "days", label: "По дням" },
  { id: "hours", label: "По часам" },
  { id: "control", label: "Контроль" },
];
const STATUS_FILTERS: StatusFilter[] = ["unresolved", "all", "new", "asked", "answered", "closed"];

export function ActivityPage() {
  const [params, setParams] = useSearchParams();
  // `?date=` — прежние ссылки на один день.
  const from = params.get("from") || params.get("date") || yesterday();
  const to = params.get("to") || params.get("date") || from;
  const вид = params.get("view");
  const canControl = useCan(ACTIVITY_CONTROL);
  const view: View = вид === "days" || вид === "hours" || (вид === "control" && canControl) ? вид : "staff";
  const views = VIEWS.filter((t) => t.id !== "control" || canControl);
  // «Контроль» без периода в адресе — 7 дней по сегодня: аномалий мало, неделя видна сразу.
  const controlTo = params.get("to") || params.get("from") || today();
  const controlFrom = params.get("from") || addDays(controlTo, -6);
  const statusRaw = params.get("status") as StatusFilter | null;
  const status: StatusFilter = statusRaw && STATUS_FILTERS.includes(statusRaw) ? statusRaw : "unresolved";
  const kindRaw = params.get("kind");
  const kind = (KINDS.some((k) => k.id === kindRaw) ? kindRaw : "") as ControlKind | "";
  // «По дням» без периода в адресе — две недели до вчера: один день там ничего не скажет.
  const daysFrom = params.get("from") || addDays(yesterday(), -13);
  const daysTo = params.get("to") || (params.get("from") ? daysFrom : yesterday());
  const user = params.get("user") || "";
  const group = (params.get("group") === "day" ? "day" : "period") as ActivityGroup;
  const set = (
    next: Partial<{
      from: string;
      to: string;
      user: string;
      group: ActivityGroup;
      view: View;
      status: StatusFilter;
      kind: ControlKind | "";
    }>,
  ) => {
    const v = { from, to, user, group, view, status, kind, ...next };
    const out = new URLSearchParams({ from: v.from });
    if (v.to !== v.from) out.set("to", v.to);
    if (v.user && (v.view === "staff" || v.view === "control")) out.set("user", v.user);
    if (v.view === "control" && v.status !== "unresolved") out.set("status", v.status);
    if (v.view === "control" && v.kind) out.set("kind", v.kind);
    if (v.group !== "period" && v.view === "staff") out.set("group", v.group);
    if (v.view !== "staff") out.set("view", v.view);
    setParams(out, { replace: true });
  };
  const report = useQuery({
    queryKey: ["activity", from, to, user, group],
    queryFn: () => loadActivity({ from, to, user, group }),
    placeholderData: keepPreviousData,
    enabled: view === "staff",
  });
  const staff = report.data?.staff ?? [];
  const userOptions = [
    { value: "", label: "все" },
    ...staff.map((s) => ({ value: String(s.user_id), label: s.name })),
    // Сотрудник из ссылки, которого нет в отчёте периода, — чтобы поле его показало.
    ...(user && !staff.some((s) => String(s.user_id) === user) ? [{ value: user, label: `#${user}` }] : []),
  ];

  return (
    <main className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5">
      <div className="flex flex-wrap items-end gap-x-6 gap-y-2 border-b border-border" data-annot="вкладки активности">
        <h1 className="pb-2 text-[16px] font-semibold tracking-[-0.015em] text-fg">Активность</h1>
        <div role="tablist" aria-label="Разделы активности" className="flex gap-1">
          {views.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={view === t.id}
              onClick={() =>
                view !== t.id &&
                (t.id === "days"
                  ? set({ view: "days", from: daysFrom, to: daysTo })
                  : t.id === "control"
                    ? set({ view: "control", from: addDays(today(), -6), to: today(), user: "", status: "unresolved", kind: "" })
                    : set({ view: t.id, from: yesterday(), to: yesterday(), user: "" }))
              }
              className={`-mb-px border-b-2 px-3 pb-2 pt-1 text-[13px] ${
                view === t.id ? "border-accent font-medium text-fg" : "border-transparent text-fg-muted hover:text-fg"
              }`}
              data-annot={`вкладка «${t.label}»`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>
      {view === "control" ? (
        <>
          <div className="flex flex-wrap items-center gap-3 text-[12.5px] text-fg-muted" data-annot="шапка «Контроль»">
            <span className="mr-auto">Аномалии смены: пропущенные, которые никто не взял, пустая линия и бездействие</span>
            <DateRangePicker
              label="Период"
              from={controlFrom}
              to={controlTo}
              windows={окна()}
              onChange={(a, b) => set({ view: "control", from: a, to: b || a })}
            />
          </div>
          <ControlView
            from={controlFrom}
            to={controlTo}
            status={status}
            kind={kind}
            user={user}
            onFilter={(f) => set({ view: "control", from: controlFrom, to: controlTo, ...f })}
          />
        </>
      ) : view === "hours" ? (
        <>
          <div className="flex flex-wrap items-center gap-2 text-[12.5px] text-fg-muted" data-annot="шапка «По часам»">
            <span className="mr-auto">Как прошёл день: звонки, лиды, люди и результат по часам</span>
            <button
              type="button"
              aria-label="Предыдущий день"
              onClick={() => set({ view: "hours", from: addDays(from, -1), to: addDays(from, -1) })}
              className="flex size-8 items-center justify-center rounded-[var(--radius-ui)] border border-border hover:bg-bg-hover"
            >
              <ChevronLeft size={15} />
            </button>
            <DateRangePicker label="День" from={from} to={from} single onChange={(a) => set({ view: "hours", from: a, to: a })} />
            <button
              type="button"
              aria-label="Следующий день"
              disabled={from >= addDays(yesterday(), 1)}
              onClick={() => set({ view: "hours", from: addDays(from, 1), to: addDays(from, 1) })}
              className="flex size-8 items-center justify-center rounded-[var(--radius-ui)] border border-border hover:bg-bg-hover disabled:opacity-40"
            >
              <ChevronRight size={15} />
            </button>
          </div>
          <HoursView day={from} />
        </>
      ) : view === "days" ? (
        <>
          <div className="flex flex-wrap items-center gap-3 text-[12.5px] text-fg-muted" data-annot="шапка «По дням»">
            <span className="mr-auto">Хватает ли людей в смене: нагрузка, как её обработали, люди и занятость по дням</span>
            <DateRangePicker label="Период" from={daysFrom} to={daysTo} windows={окна()} onChange={(a, b) => set({ view: "days", from: a, to: b || a })} />
          </div>
          <DaysView from={daysFrom} to={daysTo} />
        </>
      ) : (
      <>
      <div
        className="flex flex-wrap items-center gap-3 text-[12.5px] text-fg-muted"
        data-annot="шапка активности"
      >
        <span className="mr-auto">Сотрудники: часы, звонки, сообщения, занятость</span>
        <DateRangePicker label="Период" from={from} to={to} windows={окна()} onChange={(a, b) => set({ from: a, to: b || a })} />
        <Combo label="Сотрудник" value={user} options={userOptions} onChange={(u) => set({ user: u })} placeholder="имя сотрудника" width={240} />
        <div className="flex items-center gap-1" data-annot="группировка">
          {(Object.keys(GROUPS) as ActivityGroup[]).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => set({ group: k })}
              aria-pressed={group === k}
              className={`h-7 rounded-[var(--radius-ui)] border px-2.5 text-[12px] ${group === k ? "border-fg bg-bg text-fg" : "border-border text-fg-muted hover:bg-bg-hover"}`}
            >
              {GROUPS[k]}
            </button>
          ))}
        </div>
      </div>
      {report.isPending ? (
        <div className="text-[12.5px] text-fg-muted">Считаю по CRM… месяц — около 15 секунд</div>
      ) : report.isError ? (
        <div className="text-[12.5px] text-err">
          Не удалось получить отчёт: {String(report.error.message)}
        </div>
      ) : (
        <div className={`flex flex-col gap-4 ${report.isPlaceholderData ? "opacity-60" : ""}`}>
          <Report r={report.data} user={user} />
        </div>
      )}
      </>
      )}
    </main>
  );
}
